import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getConversionRateStats,
    ConversionRateStatsPayload,
} from '../../../services'
import { Typogaphy } from 'vnetwork-ui'
import {
    Target,
    Flame,
    Snowflake,
    Ban,
    CheckCircle2,
    Users2,
    UserCheck,
    Percent,
} from 'lucide-react'
import { ConversionRateCardSkeleton } from './skeletons'

interface ConversionRateCardProps {
    filterPayload: ConversionRateStatsPayload
}

// Compact stat card component
const StatCard: React.FC<{
    label: string
    value: string | number
    rate?: number
    icon: React.ReactNode
    color: string
    bgColor: string
}> = ({ label, value, rate, icon, color, bgColor }) => (
    <div className={`${bgColor} rounded-lg p-3 flex-1 min-w-0`}>
        <div className={`flex items-center gap-1.5 ${color} mb-1`}>
            {icon}
            <span className="text-xs truncate">{label}</span>
        </div>
        <div className="flex items-baseline gap-1">
            <Typogaphy className={`text-lg font-bold ${color}`}>
                {typeof value === 'number' ? value.toLocaleString() : value}
            </Typogaphy>
            {rate != null && (
                <span className="text-[10px] text-gray-500">
                    ({rate.toFixed(1)}%)
                </span>
            )}
        </div>
    </div>
)

const ConversionRateCard: React.FC<ConversionRateCardProps> = ({
    filterPayload,
}) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.MARKETING,
                'conversion-rate-stats',
                filterPayload,
            ],
            queryFn: () => getConversionRateStats(filterPayload),
        },
        { loadingComponent: <ConversionRateCardSkeleton /> }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const stats = data?.result
    if (!stats) return null

    return (
        <div className="bg-white rounded-md border border-gray-200 p-4 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
                <Target size={18} className="text-primary" />
                <Typogaphy className="font-medium text-primary capitalize">
                    {t('conversion-rate-stats')}
                </Typogaphy>
            </div>
            <div className="flex gap-2 mb-2">
                <StatCard
                    label={t('total-leads')}
                    value={stats.total_leads ?? 0}
                    icon={<Users2 size={14} />}
                    color="text-gray-600"
                    bgColor="bg-gray-50"
                />
                <StatCard
                    label={t('total-classified')}
                    value={stats.total_classified ?? 0}
                    icon={<UserCheck size={14} />}
                    color="text-green-600"
                    bgColor="bg-green-50"
                />
                <StatCard
                    label={t('classification-rate')}
                    value={`${(stats.classification_rate ?? 0).toFixed(1)}%`}
                    icon={<Percent size={14} />}
                    color="text-blue-600"
                    bgColor="bg-blue-50"
                />
            </div>

            {/* Lead types - 2 rows of cards to fill space */}
            <div className="flex-1 flex flex-col gap-2">
                {/* Row 1: Unclassified, Warm, SQL */}
                <div className="flex gap-2 flex-1">
                    <StatCard
                        label={t('unclassified')}
                        value={stats.unclassified ?? 0}
                        icon={<Users2 size={14} />}
                        color="text-gray-500"
                        bgColor="bg-gray-100"
                    />
                    <StatCard
                        label={t('warm-lead')}
                        value={stats.warm_lead ?? 0}
                        rate={stats.warm_conversion_rate}
                        icon={<Flame size={14} />}
                        color="text-amber-600"
                        bgColor="bg-amber-50"
                    />
                    <StatCard
                        label="SQL"
                        value={stats.sale_qualified_lead ?? 0}
                        rate={stats.sale_qualified_conversion_rate}
                        icon={<CheckCircle2 size={14} />}
                        color="text-green-600"
                        bgColor="bg-green-50"
                    />
                </div>
                {/* Row 2: Cool, Unqualified */}
                <div className="flex gap-2 flex-1">
                    <StatCard
                        label={t('cool-lead')}
                        value={stats.cool_lead ?? 0}
                        rate={stats.cool_conversion_rate}
                        icon={<Snowflake size={14} />}
                        color="text-blue-600"
                        bgColor="bg-blue-50"
                    />
                    <StatCard
                        label={t('unqualified')}
                        value={stats.un_qualified_lead ?? 0}
                        rate={stats.un_qualified_conversion_rate}
                        icon={<Ban size={14} />}
                        color="text-red-600"
                        bgColor="bg-red-50"
                    />
                </div>
            </div>
        </div>
    )
}

export default ConversionRateCard
